import { useState } from 'react'
import { api, Betriebsstoerung, gemeldet, still, stoerungAus } from './api'
import { anzahlwort, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { rufe } from './Stoerungsstelle'

/**
 * Die Anmeldung und der erzwungene Kennwortwechsel (8.1, R355–R357, R362).
 *
 * **Sie ist der einzige Bildschirm, den ein Nichtangemeldeter sieht** — und
 * der einzige, den ein Angemeldeter mit Erstkennwort sieht, bevor er es
 * gewechselt hat. Der Wechsel ist kein Hinweis, den man wegklicken kann: Ein
 * Erstkennwort aus der Betriebsdokumentation kennt jeder, der sie gelesen hat.
 *
 * **Die Absage nennt nie, was falsch war** (R357). „Anmeldename oder Kennwort
 * stimmt nicht" — wer unterscheidet, verrät, welche Namen es gibt, und ein
 * Anmeldename ist in einem Haus der Nachname.
 */

/** Was die API als Befund zurückgibt — ein Schlüssel, kein Satz (R161). */
type Befund = { meldung: Sprachschluessel; minuten?: number }

export function Anmeldemaske({ aufAnmeldung }: { aufAnmeldung: () => void }) {
  const [anmeldename, setAnmeldename] = useState('')
  const [kennwort, setKennwort] = useState('')
  const [befund, setBefund] = useState<Befund | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function anmelden(e: React.FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setBefund(null)

    try {
      const antwort = await rufe('/api/anmeldung', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ anmeldename, kennwort }),
      })

      if (antwort.ok) {
        aufAnmeldung()
        return
      }

      // Bricht der Dienst ab, ist das kein falsches Kennwort, sondern eine
      // Störung — sie steht im Störungsfenster (7.2 Abschnitt 30, R1003).
      if (antwort.status >= 500) {
        await stoerungAus(antwort)
        return
      }

      // Der Befund kommt als Schlüssel mit Werten (R145/R161) — den Satz setzt
      // die Oberfläche, und er wechselt die Sprache mit.
      const inhalt = (await antwort.json()) as { meldung?: string; minuten?: number }
      setBefund({
        meldung: (inhalt.meldung ?? 'anmeldung.falsch') as Sprachschluessel,
        minuten: inhalt.minuten,
      })
    } catch {
      // Der Dienst ist nicht erreichbar: das Störungsfenster, nicht die Maske
      // (7.2 Abschnitt 30, R1003) — es liegt auch um die Anmeldung.
      gemeldet(new Betriebsstoerung('meldung.stoerung.unerreichbar'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="anmeldehof">
      <form className="anmeldemaske" onSubmit={anmelden}>
        <h1>
          <span className="marke">CoKo</span> <span className="zusatz">{t('marke.zusatz')}</span>
        </h1>

        <label className="feld">
          <span>{t('anmeldung.name')}</span>
          <input
            type="text"
            value={anmeldename}
            autoComplete="username"
            autoFocus
            onChange={(e) => setAnmeldename(e.target.value)}
          />
        </label>

        <label className="feld">
          <span>{t('anmeldung.kennwort')}</span>
          <input
            type="password"
            value={kennwort}
            autoComplete="current-password"
            onChange={(e) => setKennwort(e.target.value)}
          />
        </label>

        {befund && (
          <p className="anmeldebefund">
            {t(befund.meldung, befund.minuten ? { minuten: anzahlwort(befund.minuten) } : undefined)}
          </p>
        )}

        <button type="submit" className="haupt" disabled={laeuft || !anmeldename || !kennwort}>
          {laeuft ? t('anmeldung.laeuft') : t('anmeldung.anmelden')}
        </button>

        {/*
          **Kein „Kennwort vergessen".** Das System verschickt keine Post
          (ADR 005) — zurückgesetzt wird vom Administrator, und das steht hier,
          damit niemand danach sucht (R145: was zu tun ist).
        */}
        <p className="anmeldehinweis">{t('anmeldung.vergessen')}</p>
      </form>
    </div>
  )
}

/**
 * Der erzwungene Kennwortwechsel (R362).
 *
 * **Vorher geht kein anderer Bildschirm auf** — die API antwortet auf jeden
 * Fachruf mit 423, und die Oberfläche zeigt statt eines weggeklickten
 * Hinweises diese Maske.
 */
export function Kennwortmaske({
  name,
  aufGewechselt,
  aufAbmelden,
}: {
  name: string
  aufGewechselt: () => void
  aufAbmelden: () => void
}) {
  const [altes, setAltes] = useState('')
  const [neues, setNeues] = useState('')
  const [nochmal, setNochmal] = useState('')
  const [befund, setBefund] = useState<Sprachschluessel | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function wechseln(e: React.FormEvent) {
    e.preventDefault()

    if (neues !== nochmal) {
      setBefund('kennwort.ungleich')
      return
    }

    setLaeuft(true)
    setBefund(null)

    try {
      const antwort = await rufe('/api/anwender/kennwort', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ altes, neues }),
      })

      if (antwort.ok) {
        aufGewechselt()
        return
      }

      if (antwort.status >= 500) {
        await stoerungAus(antwort)
        return
      }

      const inhalt = (await antwort.json()) as { meldung?: string }
      setBefund((inhalt.meldung ?? 'kennwort.altfalsch') as Sprachschluessel)
    } catch {
      gemeldet(new Betriebsstoerung('meldung.stoerung.unerreichbar'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="anmeldehof">
      <form className="anmeldemaske" onSubmit={wechseln}>
        <h1>{t('kennwort.titel')}</h1>
        <p className="anmeldehinweis">{t('kennwort.warum', { name })}</p>

        <label className="feld">
          <span>{t('kennwort.altes')}</span>
          <input
            type="password"
            value={altes}
            autoComplete="current-password"
            autoFocus
            onChange={(e) => setAltes(e.target.value)}
          />
        </label>

        <label className="feld">
          <span>{t('kennwort.neues')}</span>
          <input
            type="password"
            value={neues}
            autoComplete="new-password"
            onChange={(e) => setNeues(e.target.value)}
          />
          {/* Die Richtlinie steht **vor** dem Tippen, nicht als Absage danach
              (R145): Länge statt Zeichenklassen (R356). */}
          <small>{t('kennwort.richtlinie')}</small>
        </label>

        <label className="feld">
          <span>{t('kennwort.nochmal')}</span>
          <input
            type="password"
            value={nochmal}
            autoComplete="new-password"
            onChange={(e) => setNochmal(e.target.value)}
          />
        </label>

        {befund && <p className="anmeldebefund">{t(befund)}</p>}

        <button type="submit" className="haupt" disabled={laeuft || !altes || !neues}>
          {laeuft ? t('anmeldung.laeuft') : t('kennwort.wechseln')}
        </button>

        <button
          type="button"
          onClick={async () => {
            // Abmelden geht auch ohne Dienst — kein Fenster dafür.
            await api.abmelden().catch((f) => void still(f))
            aufAbmelden()
          }}
        >
          {t('anmeldung.abmelden')}
        </button>
      </form>
    </div>
  )
}
